import { classFlow, classStepComplete, classStepsForFlow } from '@/state/classSteps';

export const SEAT_BACKUP_PREFIX = 'ba-seat-backup:';

const unicodeLetter = /^\p{L}$/u;

export function normalizeSeatDisplayName(value) {
  if (typeof value !== 'string') return null;
  const parts = value.trim().split(/\s+/);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  let [first, initial] = parts;
  if (initial.endsWith('.')) initial = initial.slice(0, -1);
  const lastLetters = [...initial];
  if (lastLetters.length < 1 || lastLetters.length > 2 || lastLetters.some((char) => !unicodeLetter.test(char))) return null;
  if (first[0] === '-' || first[0] === "'" || first.at(-1) === '-' || first.at(-1) === "'") return null;
  if (first.includes('--') || first.includes("''")) return null;
  const letters = [...first].filter((char) => char !== '-' && char !== "'");
  if (letters.length < 1 || letters.length > 16 || letters.some((char) => !unicodeLetter.test(char))) return null;
  if ([...first].some((char) => char !== '-' && char !== "'" && !unicodeLetter.test(char))) return null;
  let capitalizeNext = true;
  first = [...first].map((char) => {
    if (char === '-' || char === "'") {
      capitalizeNext = true;
      return char;
    }
    const result = capitalizeNext ? char.toUpperCase() : char.toLowerCase();
    capitalizeNext = false;
    return result;
  }).join('');
  initial = lastLetters.map((char, index) => (index === 0 ? char.toUpperCase() : char.toLowerCase())).join('');
  return `${first} ${initial}.`;
}

export function showcaseNoticeKey(seatId, tokenVersion) {
  return `ba-showcase-notice:${seatId}:${tokenVersion}`;
}

export function seatBackupKey(seatId, tokenVersion, slot) {
  return `${SEAT_BACKUP_PREFIX}${seatId}:${tokenVersion}:${slot}`;
}

export function purgeSeatBackups(storage = sessionStorage, keepIdentity = null) {
  const keepPrefix = keepIdentity
    ? `${SEAT_BACKUP_PREFIX}${keepIdentity.seatId}:${keepIdentity.tokenVersion}:`
    : null;
  Object.keys(storage).forEach((key) => {
    if (key.startsWith(SEAT_BACKUP_PREFIX) && (!keepPrefix || !key.startsWith(keepPrefix))) {
      storage.removeItem(key);
    }
  });
}

export function readSeatBackup(storage, seatId, tokenVersion, slot) {
  try {
    const raw = storage.getItem(seatBackupKey(seatId, tokenVersion, slot));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.arrangement || !('lyrics' in parsed)) return null;
    return { arrangement: parsed.arrangement, lyrics: parsed.lyrics };
  } catch (error) {
    return null;
  }
}

export function writeSeatBackup(storage, seatId, tokenVersion, slot, snapshot) {
  storage.setItem(seatBackupKey(seatId, tokenVersion, slot), JSON.stringify({
    arrangement: snapshot.arrangement,
    lyrics: snapshot.lyrics,
  }));
}

export function clearSeatBackup(storage, seatId, tokenVersion, slot) {
  storage.removeItem(seatBackupKey(seatId, tokenVersion, slot));
}

export function deriveFurthestClassStep(arrangement = {}) {
  const flow = classFlow(arrangement.classSession);
  const steps = classStepsForFlow(flow);
  const skipped = new Set(flow === 2 && Array.isArray(arrangement.classSession?.progress?.skipped)
    ? arrangement.classSession.progress.skipped : []);
  let furthest = 0;
  for (let index = 0; index < steps.length - 1; index += 1) {
    const stepId = steps[index].id;
    if (!skipped.has(stepId) && !classStepComplete(stepId, {
      tracks: arrangement.tracks || [],
      sectionMarkers: arrangement.sectionMarkers || [],
      bpm: arrangement.bpm || 90,
      classSession: arrangement.classSession,
    })) break;
    furthest = index + 1;
  }
  return furthest;
}

export function seatSnapshot(arrangement, lyrics, bpm, totalBars) {
  return {
    arrangement: {
      tracks: arrangement.tracks || [],
      sectionMarkers: arrangement.sectionMarkers || [],
      classSession: arrangement.classSession,
      bpm,
      totalBars,
    },
    lyrics,
  };
}
